import {
  faThumbsDown as faThumbsDownSolid,
  faThumbsUp as faThumbsUpSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-thin-svg-icons';
import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../shared/components/button/Button';
import { Tooltip } from '../../shared/components/tooltip/Tooltip';
import { WhiteBackgroundModal } from '../../shared/modal/whitePopUp/WhiteBackgroundModal';

export function RateBlock({
  like,
  dislike,
  setLike,
  setDislike,
  handleLikeDislike,
  setLikeDislikeOpen,
  likeDislikeOpen,
}) {
  const { t } = useTranslation();

  const rateBlockRef = useRef(null);
  const lastActiveElementRef = useRef(null);
  const dislikeButtonRef = useRef(null);

  const updateReaction = (id) => {
    const isLike = id === 'like';
    const isDislike = id === 'dislike';

    setLike(isLike ? !like : !!(like && dislike));
    setDislike(isDislike ? !dislike : !!(dislike && like));

    if (like && isDislike) {
      setLike(false);
      setDislike(true);
    }
    if (dislike && isLike) {
      setDislike(false);
      setLike(true);
    }
    if (!like && !dislike) {
      isDislike ? setDislike(true) : setLike(true);
    }

    setLikeDislikeOpen(false);
    handleLikeDislike(id);
  };

  useEffect(() => {
    if (!likeDislikeOpen) return;

    lastActiveElementRef.current = document.activeElement;
    dislikeButtonRef.current?.focus();

    const handleKeyDown = (e) => {
      const focusableElements = rateBlockRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements?.length) return;

      const firstEl = focusableElements[0];
      const lastEl = focusableElements[focusableElements.length - 1];

      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      lastActiveElementRef.current?.focus();
    };
  }, [likeDislikeOpen]);

  const getSomeIcon = (id) => {
    if (id === 'dislike') {
      if (dislike) return faThumbsDownSolid;
      return faThumbsDown;
    }
    if (like) return faThumbsUpSolid;
    return faThumbsUp;
  };

  return (
    <div className="rate-block-container" ref={rateBlockRef}>
      {likeDislikeOpen && <div className="rate-overlay" />}
      {likeDislikeOpen && (
        <WhiteBackgroundModal
          hasCloseIcon
          id="like-dislike-modal"
          title={{ text: t('M022'), showTitle: true }}
          size="mini"
          handleClose={() => setLikeDislikeOpen(false)}
          dialogTitle={t('modal.rating.info')}
        >
          <div className="rate-block-modal-content">
            <p className="rate-block-modal-title">{t('rate.block.modal.title')}</p>
            <div className="rate-block-modal-buttons">
              {['dislike', 'like'].map((id) => (
                <Tooltip key={id} content={t(id === 'dislike' ? 'M099' : 'M100')}>
                  <Button
                    key={id}
                    id={id}
                    variant="icon-only"
                    color="dark"
                    onClick={() => updateReaction(id)}
                    ariaLabel={`${(id === 'like' && like) || (id === 'dislike' && dislike) ? t('rate.voted') : t('rate.clickToChange')} ${t(id === 'dislike' ? 'M099' : 'M100')}`}
                    ref={id === 'dislike' ? dislikeButtonRef : undefined}
                    icon={{ faIconDefinition: getSomeIcon(id), size: '3x', color: 'dark' }}
                  />
                </Tooltip>
              ))}
            </div>
            {(like || dislike) && (
              <p className="rate-block-modal-title">
                {like ? t('rate.voted.info.like') : t('rate.voted.info.dislike')}
              </p>
            )}
          </div>
        </WhiteBackgroundModal>
      )}
    </div>
  );
}
